import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, CircularProgress,
  Chip, Grid, Tooltip, Menu, MenuItem, Button, IconButton
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { PiFileCsv } from 'react-icons/pi';
import { fetchInvoiceDiscrepancy, fetchInvoiceDetailByDoc, fetchMatchedSofDocs } from '../../api/dashboardApi';
import MatchedSofDocsModal from '../Dashboard/MatchedSofDocsModal';
import ContractAmountBreakdownModal from '../Dashboard/ContractAmountBreakdownModal';

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index} style={{ display: value === index ? 'block' : 'none' }}>
      {value === index && <Box sx={{ pt: 1 }}>{children}</Box>}
    </div>
  );
}

const formatCurrency = (val, prefix = '') => {
  if (val === null || val === undefined || val === '') return '—';
  const num = Number(val);
  if (isNaN(num)) return val;
  const isNegative = num < 0;
  const absNum = Math.abs(num);
  return `${isNegative ? '-' : ''}${prefix}${absNum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const CompactTable = ({ columns, rows, emptyMessage = "No data available", dense = false }) => (
  <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', maxHeight: 400 }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          {columns.map((col, i) => (
            <TableCell key={i} sx={{ fontWeight: 700, fontSize: dense ? '10px' : '11px', backgroundColor: '#f8fafc', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', py: dense ? 0.5 : 1, px: dense ? 1 : 2, whiteSpace: 'nowrap' }}>
              {col.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: '#94a3b8', fontSize: '13px' }}>
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row, i) => (
            <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
              {columns.map((col, j) => (
                <TableCell key={j} sx={{ fontSize: dense ? '11px' : '12px', py: dense ? 0.3 : 0.8, px: dense ? 1 : 2, color: '#334155', whiteSpace: dense ? 'nowrap' : 'normal' }}>
                  {col.render ? col.render(row[col.field], row) : (row[col.field] ?? '—')}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

// --- Separate Tab Components ---

const GroupedServiceTab = ({ data, localCurrency }) => {
  const [sofModalOpen, setSofModalOpen] = useState(false);
  const [sofModalData, setSofModalData] = useState(null);
  const [sofModalLoading, setSofModalLoading] = useState(false);
  const [openBreakdown, setOpenBreakdown] = useState(false);
  const [breakdownData, setBreakdownData] = useState({});

  const handleClassificationClick = async (serviceId, invoiceId) => {
    if (!serviceId || !invoiceId) return;
    setSofModalOpen(true);
    setSofModalLoading(true);
    try {
      const resp = await fetchMatchedSofDocs(serviceId, invoiceId);
      setSofModalData(resp);
    } catch (err) {
      console.error("Failed to fetch matched SOF docs", err);
      setSofModalData({ status: false, msg: "Failed to fetch data." });
    } finally {
      setSofModalLoading(false);
    }
  };

  const columns = [
    { 
      field: 'service_id', 
      headerName: 'Service ID', 
      width: 100,
      renderCell: (params) => (
        <span 
          onClick={() => handleClassificationClick(params.row.service_id, params.row.invoice_id)}
          style={{ cursor: "pointer", borderBottom: "1px dashed #7C2EA0", color: "#1976d2" }}
        >
          {params.value || "-"}
        </span>
      )
    },
    { field: 'doc_id', headerName: 'Doc ID', description: 'Document ID', width: 90 },
    { field: 'invoice_currency', headerName: 'Currency', description: 'Invoice Currency', width: 110 },
    { field: 'invoice_local_amount', headerName: 'Inv Local Amt', description: 'Invoice Local Amount', width: 120, renderCell: (params) => formatCurrency(params.value, localCurrency ? `${localCurrency} ` : '') },
    { field: 'invoice_rate_amount_usd', headerName: 'Inv Rate (USD)', description: 'Invoice Rate Amount (USD)', width: 130, renderCell: (params) => formatCurrency(params.value, 'USD ') },
    { field: 'invoice_discount_usd', headerName: 'Inv Disc (USD)', description: 'Invoice Discount (USD)', width: 130, renderCell: (params) => formatCurrency(params.value, 'USD ') },
    { field: 'amount_usd', headerName: 'Amt (USD)', description: 'Amount (USD)', width: 110, renderCell: (params) => formatCurrency(params.value, 'USD ') },
    { field: 'discrepancy_classification', headerName: 'Class.', description: 'Classification', width: 130, renderCell: (params) => (
      params.value ? (
        <Tooltip title={params.row.discrepancy_classification_description || ""} arrow placement="top">
          <span style={{ cursor: params.row.discrepancy_classification_description ? "help" : "default", borderBottom: params.row.discrepancy_classification_description ? "1px dashed #7C2EA0" : "none" }}>
            {String(params.value).replace(/_/g, ' ')}
          </span>
        </Tooltip>
      ) : '—'
    )},
    { 
      field: 'contract_amount', 
      headerName: 'Contract Amt', 
      description: 'Contract Amount', 
      width: 120, 
      renderCell: (params) => {
        const hasData =
          (params.row.contract_amount_breakdown?.sof_items?.length > 0) ||
          (params.row.contract_amount_breakdown?.amendment_items?.length > 0);

        return (
          <span
            style={{
              color: hasData ? "blue" : "gray",
              textDecoration: hasData ? "underline" : "none",
              cursor: hasData ? "pointer" : "not-allowed",
            }}
            onClick={() => {
              if (!hasData) return;
              const rawBreakdown = {
                sof: params.row.contract_amount_breakdown.sof_items || [],
                amendment: params.row.contract_amount_breakdown.amendment_items || [],
                contract_calculation: params.row.contract_amount_breakdown.contract_calculation || null,
                total_sof: params.row.contract_amount_breakdown.total_sof,
                total_amendment: params.row.contract_amount_breakdown.total_amendment,
                contract_amount: params.row.contract_amount,
                discrepancy_explanation: params.row.discrepancy_explanation || null,
              };
              setBreakdownData(rawBreakdown);
              setOpenBreakdown(true);
            }}
          >
            {formatCurrency(params.value, 'USD ')}
          </span>
        );
      }
    },
    { field: 'discrepancy_amount', headerName: 'Discrepancy Amt', description: 'Discrepancy Amount', width: 160, renderCell: (params) => <span style={{ color: Number(params.value) > 0 ? '#dc2626' : Number(params.value) < 0 ? '#16a34a' : '#64748b', fontWeight: 600 }}>{formatCurrency(params.value, 'USD ')}</span> },
  ];
  return (
    <Box sx={{ height: 400, width: '100%', mt: 1 }}>
      <DataGrid 
        rows={data} 
        columns={columns} 
        density="compact"
        disableRowSelectionOnClick
        pageSizeOptions={[5, 10, 25]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 },
          },
        }}
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f8fafc',
            color: '#475569',
            fontSize: '11px',
            textTransform: 'uppercase',
            fontWeight: 700,
            letterSpacing: '0.05em',
            minHeight: '40px !important',
            maxHeight: '40px !important',
          },
          '& .MuiDataGrid-cell': {
            fontSize: '11px',
            color: '#334155'
          }
        }}
      />
      <MatchedSofDocsModal
        open={sofModalOpen}
        onClose={() => setSofModalOpen(false)}
        data={sofModalData}
        isLoading={sofModalLoading}
      />
      <ContractAmountBreakdownModal 
        open={openBreakdown}
        onClose={() => setOpenBreakdown(false)}
        breakdownData={breakdownData}
      />
    </Box>
  );
};

const LineItemTab = ({ data, loading, localCurrency }) => {
  const columns = [
    { field: 'service_id', headerName: 'Service ID', width: 110 },
    { field: 'service_name', headerName: 'Service Name', width: 140, renderCell: (params) => params.row.service_name || params.row.service_name_y || '—' },
    { field: 'service_description', headerName: 'Description', description: 'Service Description', width: 150 },
    { field: 'doc_id', headerName: 'Doc ID', description: 'Document ID', width: 110 },
    { field: 'charge_type', headerName: 'Charge Type', width: 120 },
    { field: 'amount', headerName: 'Amount', width: 110, renderCell: (params) => formatCurrency(params.value, localCurrency ? `${localCurrency} ` : '') },
    { field: 'amount_usd', headerName: 'Amt (USD)', description: 'Amount (USD)', width: 110, renderCell: (params) => formatCurrency(params.value, 'USD ') },
    { field: 'discount_&_promotion', headerName: 'Disc & Promo', description: 'Discount & Promotion', width: 130, renderCell: (params) => formatCurrency(params.value, localCurrency ? `${localCurrency} ` : '') },
    { field: 'discount_usd', headerName: 'Disc (USD)', description: 'Discount (USD)', width: 110, renderCell: (params) => formatCurrency(params.value, 'USD ') },
    { field: 'taxes_&_surcharges', headerName: 'Taxes & Sur.', description: 'Taxes & Surcharges (USD)', width: 130, renderCell: (params) => formatCurrency(params.value, localCurrency ? `${localCurrency} ` : '') },
    { field: 'taxes_usd', headerName: 'Taxes (USD)', width: 110, renderCell: (params) => formatCurrency(params.value, 'USD ') },
    { field: 'total_usd', headerName: 'Total Amt', description: 'Total Amount', width: 120, renderCell: (params) => formatCurrency(params.value, localCurrency ? `${localCurrency} ` : '') },
    { 
      field: 'conversion_rate', 
      headerName: 'Conv Rate', 
      description: 'Conversion Rate', 
      width: 110,
      renderCell: (params) => {
        const val = params.value;
        if (val === null || val === undefined || val === "N/A" || val === "") return "—";
        const num = Number(val);
        if (isNaN(num)) return val;
        const formatted = parseFloat(num.toFixed(3)).toString();
        return (
          <Tooltip title={String(val)} arrow placement="top">
            <span style={{ cursor: "pointer", borderBottom: "1px dashed #7C2EA0" }}>
              {formatted}
            </span>
          </Tooltip>
        );
      }
    },
    { field: 'converted_total_usd', headerName: 'Total USD', width: 120, renderCell: (params) => formatCurrency(params.value, 'USD ') },
    { field: 'loc_name', headerName: 'Location', width: 130, renderCell: (params) => params.row.loc_name || params.row.location || '—' },
    { field: 'location_id', headerName: 'Loc ID', description: 'Location ID', width: 110, renderCell: (params) => params.row.location_id || params.row.location_id_y || params.row.location_id_x || '—' },
    { field: 'country', headerName: 'Country', width: 110 },
    { field: 'page_no', headerName: 'Page No', width: 100 },
  ];

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>;
  return (
    <Box sx={{ height: 400, width: '100%', mt: 1 }}>
      <DataGrid 
        rows={data} 
        columns={columns} 
        density="compact"
        disableRowSelectionOnClick
        pageSizeOptions={[5, 10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10, page: 0 } },
        }}
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f8fafc',
            color: '#475569',
            fontSize: '11px',
            textTransform: 'uppercase',
            fontWeight: 700,
            letterSpacing: '0.05em',
            minHeight: '40px !important',
            maxHeight: '40px !important',
          },
          '& .MuiDataGrid-cell': {
            fontSize: '11px',
            color: '#334155'
          }
        }}
      />
    </Box>
  );
};

const CircuitTab = ({ data, localCurrency }) => {
  const columns = [
    { field: 'circuit_id', headerName: 'Circuit ID', width: 130 },
    { field: 'amount_sum', headerName: 'Amount', width: 130, renderCell: (params) => formatCurrency(params.value, localCurrency ? `${localCurrency} ` : '') },
    { field: 'amount_usd_sum', headerName: 'Amt (USD)', width: 130, renderCell: (params) => formatCurrency(params.value, 'USD ') },
    { field: 'discount_and_promotion', headerName: 'Disc & Promo', description: 'Discount & Promotion', width: 140, renderCell: (params) => formatCurrency(params.value, localCurrency ? `${localCurrency} ` : '') },
    { field: 'taxes_and_surcharges', headerName: 'Taxes & Sur.', width: 140, renderCell: (params) => formatCurrency(params.value, localCurrency ? `${localCurrency} ` : '') },
    { field: 'total_usd', headerName: 'Total Amt', width: 130, description: 'Total Amount (Local)', renderCell: (params) => formatCurrency(params.value, localCurrency ? `${localCurrency} ` : '') },
    { field: 'converted_total_usd', headerName: 'Total USD', width: 130, renderCell: (params) => formatCurrency(params.value, 'USD ') },
  ];

  return (
    <Box sx={{ height: 400, width: '100%', mt: 1 }}>
      <DataGrid 
        rows={data} 
        columns={columns} 
        density="compact"
        disableRowSelectionOnClick
        pageSizeOptions={[5, 10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10, page: 0 } },
        }}
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f8fafc',
            color: '#475569',
            fontSize: '11px',
            textTransform: 'uppercase',
            fontWeight: 700,
            letterSpacing: '0.05em',
            minHeight: '40px !important',
            maxHeight: '40px !important',
          },
          '& .MuiDataGrid-cell': {
            fontSize: '11px',
            color: '#334155'
          }
        }}
      />
    </Box>
  );
};

const LocationTab = ({ data, loading, localCurrency }) => {
  const columns = [
    { field: 'location_id', headerName: 'Location ID', width: 140,
      renderCell: (params) => (
        <span style={{ fontWeight: params.row.isTotal ? 700 : 400 }}>
          {params.row.isTotal ? `Total ${params.value}` : params.value}
        </span>
      )
    },
    { field: 'loc_name', headerName: 'Location', width: 200,
      renderCell: (params) => (
        <Tooltip title={params.value || ''} arrow placement="top">
          <span style={{ fontWeight: params.row.isTotal ? 700 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {params.row.isTotal ? '' : (params.value || '—')}
          </span>
        </Tooltip>
      )
    },
    { field: 'service_name', headerName: 'Service', width: 180,
      renderCell: (params) => (
        <span style={{ fontWeight: params.row.isTotal ? 700 : 400 }}>
          {params.row.isTotal ? '' : (params.value || '—')}
        </span>
      )
    },
    { field: 'amount', headerName: 'Amount', width: 130,
      renderCell: (params) => <span style={{ fontWeight: params.row.isTotal ? 700 : 400 }}>{formatCurrency(params.value, localCurrency ? `${localCurrency} ` : '')}</span>
    },
    { field: 'amount_usd', headerName: 'Amt (USD)', width: 130,
      renderCell: (params) => <span style={{ fontWeight: params.row.isTotal ? 700 : 400 }}>{formatCurrency(params.value, 'USD ')}</span>
    },
    { field: 'discount', headerName: 'Disc & Promo', description: 'Discounts & Promotions', width: 140,
      renderCell: (params) => <span style={{ fontWeight: params.row.isTotal ? 700 : 400 }}>{formatCurrency(params.value, localCurrency ? `${localCurrency} ` : '')}</span>
    },
    { field: 'discount_usd', headerName: 'Disc (USD)', description: 'Discounts & Promotions (USD)', width: 140,
      renderCell: (params) => <span style={{ fontWeight: params.row.isTotal ? 700 : 400 }}>{formatCurrency(params.value, 'USD ')}</span>
    },
    { field: 'taxes', headerName: 'Taxes & Sur.', description: 'Taxes & Surcharges', width: 140,
      renderCell: (params) => <span style={{ fontWeight: params.row.isTotal ? 700 : 400 }}>{formatCurrency(params.value, localCurrency ? `${localCurrency} ` : '')}</span>
    },
    { field: 'taxes_usd', headerName: 'Taxes (USD)', description: 'Taxes & Surcharges (USD)', width: 140,
      renderCell: (params) => <span style={{ fontWeight: params.row.isTotal ? 700 : 400 }}>{formatCurrency(params.value, 'USD ')}</span>
    },
    { field: 'total_usd', headerName: 'Total (USD)', width: 140,
      renderCell: (params) => <span style={{ fontWeight: params.row.isTotal ? 700 : 400 }}>{formatCurrency(params.value, 'USD ')}</span>
    },
  ];

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>;
  return (
    <Box sx={{ height: 400, width: '100%', mt: 1 }}>
      <DataGrid
        rows={data}
        columns={columns}
        density="compact"
        disableRowSelectionOnClick
        disableColumnSorting
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
        }}
        getRowClassName={(params) => params.row.isTotal ? 'location-total-row' : ''}
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f8fafc',
            color: '#475569',
            fontSize: '11px',
            textTransform: 'uppercase',
            fontWeight: 700,
            letterSpacing: '0.05em',
            minHeight: '40px !important',
            maxHeight: '40px !important',
          },
          '& .MuiDataGrid-cell': {
            fontSize: '11px',
            color: '#334155'
          },
          '& .location-total-row': {
            backgroundColor: '#f1f5f9 !important',
            borderTop: '1px solid #cbd5e1',
          },
        }}
      />
    </Box>
  );
};

// --- Main Component ---

const InvoiceDetailView = ({ invoice, onOpenPdf }) => {
  const [tabValue, setTabValue] = useState(0);
  const [discrepancyData, setDiscrepancyData] = useState([]);
  const [circuitTotals, setCircuitTotals] = useState([]);
  const [groupedServices, setGroupedServices] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [loadingDisc, setLoadingDisc] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);
  
  // For Invoice PDF Menu
  const [anchorEl, setAnchorEl] = useState(null);
  
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleOpenDialog = () => {
    handleMenuClose();
    if (onOpenPdf && invoice.enc_name) onOpenPdf(invoice.enc_name);
  };

  const handleOpenWindow = () => {
    handleMenuClose();
    if (invoice.enc_name) window.open(invoice.enc_name, '_blank', 'width=1000,height=800,scrollbars=yes,resizable=yes');
  };

  useEffect(() => {
    if (!invoice?.invoice_id) return;

    setLoadingDisc(true);
    fetchInvoiceDiscrepancy(invoice.invoice_id)
      .then((res) => {
        let rawData = res.data;
        let items = [], circuits = [], grouped = [];

        if (Array.isArray(rawData)) {
          items = rawData;
          if (res.circuit_totals && Array.isArray(res.circuit_totals)) circuits = res.circuit_totals;
          if (res.grouped_services && Array.isArray(res.grouped_services)) grouped = res.grouped_services;
        } else if (rawData && typeof rawData === 'object') {
          items = rawData.data || [];
          circuits = rawData.circuit_totals || [];
          grouped = rawData.grouped_services || [];
        }

        setDiscrepancyData(items.map((el, idx) => ({ ...el, id: idx, service_name_y: el.service_name_y || el.service_name_x, location_id_y: el.location_id_y || el.location_id_x })));
        setCircuitTotals(circuits.map((el, idx) => ({ ...el, id: idx })));
        setGroupedServices(grouped.map((el, idx) => ({ ...el, id: idx })));
      })
      .catch(() => {
        setDiscrepancyData([]);
        setCircuitTotals([]);
        setGroupedServices([]);
      })
      .finally(() => setLoadingDisc(false));

    if (invoice.id) {
      setLoadingLines(true);
      fetchInvoiceDetailByDoc(invoice.id)
        .then((res) => setLineItems(res.data || []))
        .catch(() => setLineItems([]))
        .finally(() => setLoadingLines(false));
    }
  }, [invoice?.invoice_id, invoice?.id]);

  const locationData = useMemo(() => {
    const locMap = {};
    discrepancyData.forEach((item) => {
      const locId = item.location_id || item.location_id_y || item.location_id_x || 'Unknown';
      const locName = item.loc_name || '';
      const svcName = item.service_name || 'Unknown';
      const key = `${locId}__${svcName}`;

      if (!locMap[key]) {
        locMap[key] = {
          location_id: locId,
          loc_name: locName,
          service_name: svcName,
          amount: 0,
          amount_usd: 0,
          discount: 0,
          discount_usd: 0,
          taxes: 0,
          taxes_usd: 0,
          total_usd: 0,
        };
      }
      locMap[key].amount += Number(item.amount || 0);
      locMap[key].amount_usd += Number(item.amount_usd || 0);
      locMap[key].discount += Number(item['discount_&_promotion'] || 0);
      locMap[key].discount_usd += Number(item.discount_usd || 0);
      locMap[key].taxes += Number(item['taxes_&_surcharges'] || item.taxes || 0);
      locMap[key].taxes_usd += Number(item.taxes_usd || 0);
      locMap[key].total_usd += Number(item.converted_total_usd || 0);
    });

    // Group by location and build rows with totals
    const rows = [];
    const locGroups = {};
    Object.values(locMap).forEach((row) => {
      if (!locGroups[row.location_id]) locGroups[row.location_id] = [];
      locGroups[row.location_id].push(row);
    });

    let idx = 0;
    Object.entries(locGroups).forEach(([locId, services]) => {
      let totalAmt = 0, totalAmtUsd = 0, totalDisc = 0, totalDiscUsd = 0, totalTax = 0, totalTaxUsd = 0, totalUsd = 0;
      services.forEach((svc) => {
        rows.push({ id: idx++, ...svc, isTotal: false });
        totalAmt += svc.amount;
        totalAmtUsd += svc.amount_usd;
        totalDisc += svc.discount;
        totalDiscUsd += svc.discount_usd;
        totalTax += svc.taxes;
        totalTaxUsd += svc.taxes_usd;
        totalUsd += svc.total_usd;
      });
      rows.push({
        id: idx++,
        location_id: locId,
        loc_name: '',
        service_name: '',
        amount: totalAmt,
        amount_usd: totalAmtUsd,
        discount: totalDisc,
        discount_usd: totalDiscUsd,
        taxes: totalTax,
        taxes_usd: totalTaxUsd,
        total_usd: totalUsd,
        isTotal: true,
      });
    });
    return rows;
  }, [discrepancyData]);

  const rawConv = (discrepancyData.length > 0 && discrepancyData[0].conversion_rate) 
    ? Number(discrepancyData[0].conversion_rate) 
    : (Number(invoice.total_amount_raw) > 0 ? (Number(invoice.total_usd_raw) / Number(invoice.total_amount_raw)) : null);

  const infoItems = [
    { 
      label: 'Invoice No', 
      value: invoice.enc_name ? (
        <>
          <span 
            onClick={handleMenuClick} 
            style={{ color: '#0ea5e9', cursor: 'pointer', textDecoration: 'underline' }}
          >
            {invoice.invoice_id}
          </span>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={handleOpenDialog} sx={{ fontSize: '13px' }}>Open in dialog</MenuItem>
            <MenuItem onClick={handleOpenWindow} sx={{ fontSize: '13px' }}>Open in window</MenuItem>
          </Menu>
        </>
      ) : invoice.invoice_id
    },
    { label: 'Account No', value: invoice.account_no },
    { label: 'Customer ID', value: invoice.customer_id },
    { label: 'Invoice Date', value: invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString('en-GB') : '—' },
    { label: 'Vendor', value: invoice.vendor_name },
    { label: 'Currency', value: <Chip label={invoice.currency || '—'} size="small" sx={{ bgcolor: '#e0e7ff', color: '#4f46e5', height: 20, fontSize: '11px', fontWeight: 700, borderRadius: 1 }} /> },
    { 
      label: 'Conversion', 
      value: rawConv !== null ? (
        <Tooltip title={String(rawConv)} arrow placement="top">
          <Chip label={rawConv.toFixed(3)} size="small" sx={{ bgcolor: '#e0e7ff', color: '#4f46e5', height: 20, fontSize: '11px', fontWeight: 700, borderRadius: 1 }} />
        </Tooltip>
      ) : <Chip label="—" size="small" sx={{ bgcolor: '#e0e7ff', color: '#4f46e5', height: 20, fontSize: '11px', fontWeight: 700, borderRadius: 1 }} />
    },
    { label: 'Format', value: invoice.format },
  ];

  const summaryItems = [
    { label: 'NET Amount', value: `${invoice.currency ? `${invoice.currency} ` : ''}${formatCurrency(invoice.total_amount)}`, color: '#1e293b' },
    { 
      label: 'NET Amount(USD)', 
      value: `USD ${formatCurrency(invoice.total_usd)}`, 
      color: '#0369a1',
      tooltip: `${invoice.currency ? `${invoice.currency} ` : ''}${formatCurrency(invoice.total_amount)} × ${rawConv != null ? Number(rawConv).toFixed(3) : 1} = USD ${formatCurrency(invoice.total_usd)}`
    },
    { label: 'Discrepancy', value: `USD ${formatCurrency(invoice.grouped_discrepancy_amount)}`, color: Number(invoice.grouped_discrepancy_amount) > 0 ? '#dc2626' : '#16a34a' },
    { label: 'Settled', value: `USD ${formatCurrency(invoice.total_adj)}`, color: '#059669' },
    { label: 'Final amount', value: `USD ${formatCurrency(invoice.grouped_discrepancy_final_amount)}`, color: Number(invoice.grouped_discrepancy_final_amount) > 0 ? '#dc2626' : '#16a34a' },
  ];

  const tabs = [
    { label: 'Grouped Service', show: groupedServices.length > 0 },
    { label: 'Line Item', show: true },
    { label: 'Circuit', show: circuitTotals.length > 0 },
    { label: 'Location', show: true },
  ];

  const visibleTabs = tabs.filter(t => t.show);

  const getTabContent = (tabLabel) => {
    switch (tabLabel) {
      case 'Grouped Service':
        return <GroupedServiceTab data={groupedServices} localCurrency={invoice.currency} />;
      case 'Line Item':
        return <LineItemTab data={discrepancyData} loading={loadingDisc} localCurrency={invoice.currency} />;
      case 'Circuit':
        return <CircuitTab data={circuitTotals} localCurrency={invoice.currency} />;
      case 'Location':
        return <LocationTab data={locationData} loading={loadingDisc} localCurrency={invoice.currency} />;
      default:
        return null;
    }
  };

  const handleExportCSV = () => {
    const activeTab = visibleTabs[tabValue]?.label;
    if (!activeTab) return;
    
    let dataToExport = [];
    let headers = [];

    if (activeTab === 'Grouped Service') {
      dataToExport = groupedServices;
      headers = ['Service ID', 'Document ID', 'Invoice Currency', 'Invoice Local Amount', 'Invoice Rate Amount (USD)', 'Invoice Discount (USD)', 'Amount (USD)', 'Classification', 'Contract Amount', 'Discrepancy Amount'];
    } else if (activeTab === 'Line Item') {
      dataToExport = discrepancyData;
      headers = ['Service ID', 'Service Name', 'Service Description', 'Document ID', 'Charge Type', 'Amount', 'Amt (USD)', 'Disc & Promo', 'Disc (USD)', 'Taxes & Sur.', 'Taxes (USD)', 'Total Amt', 'Total USD', 'Location', 'Loc ID', 'Country', 'Page No'];
    } else if (activeTab === 'Circuit') {
      dataToExport = circuitTotals;
      headers = ['Circuit ID', 'Amount', 'Amount (USD)', 'Discount & Promo', 'Taxes & Surcharges', 'Total Amount', 'Total USD'];
    } else if (activeTab === 'Location') {
      dataToExport = locationData;
      headers = ['Location ID', 'Location', 'Service', 'Amount', 'Amt (USD)', 'Disc & Promo', 'Disc (USD)', 'Taxes & Sur.', 'Taxes (USD)', 'Total (USD)'];
    }

    if (dataToExport.length === 0) return;

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    let csvContent = headers.join(',') + '\n';
    
    dataToExport.forEach(row => {
      let rowData = [];
      const locCur = invoice.currency ? `${invoice.currency} ` : '';
      
      if (activeTab === 'Grouped Service') {
        rowData = [
          row.service_id, row.doc_id, row.invoice_currency, formatCurrency(row.invoice_local_amount, locCur), formatCurrency(row.invoice_rate_amount_usd, 'USD '), formatCurrency(row.invoice_discount_usd, 'USD '), formatCurrency(row.amount_usd, 'USD '), row.discrepancy_classification, formatCurrency(row.contract_amount, 'USD '), formatCurrency(row.discrepancy_amount, 'USD ')
        ];
      } else if (activeTab === 'Line Item') {
        rowData = [
          row.service_id, row.service_name, row.service_description, row.doc_id, row.charge_type, formatCurrency(row.amount, locCur), formatCurrency(row.amount_usd, 'USD '), formatCurrency(row['discount_&_promotion'], locCur), formatCurrency(row.discount_usd, 'USD '), formatCurrency(row['taxes_&_surcharges'], locCur), formatCurrency(row.taxes_usd, 'USD '), formatCurrency(row.total_usd, locCur), formatCurrency(row.converted_total_usd, 'USD '), (row.loc_name || row.location), (row.location_id || row.location_id_y || row.location_id_x), row.country, row.page_no
        ];
      } else if (activeTab === 'Circuit') {
        rowData = [
          row.circuit_id, formatCurrency(row.amount_sum, locCur), formatCurrency(row.amount_usd_sum, 'USD '), formatCurrency(row.discount_and_promotion, locCur), formatCurrency(row.taxes_and_surcharges, locCur), formatCurrency(row.total_usd, locCur), formatCurrency(row.converted_total_usd, 'USD ')
        ];
      } else if (activeTab === 'Location') {
        rowData = [
          row.isTotal ? `Total ${row.location_id}` : row.location_id, row.isTotal ? '' : row.loc_name, row.isTotal ? '' : row.service_name, formatCurrency(row.amount, locCur), formatCurrency(row.amount_usd, 'USD '), formatCurrency(row.discount, locCur), formatCurrency(row.discount_usd, 'USD '), formatCurrency(row.taxes, locCur), formatCurrency(row.taxes_usd, 'USD '), formatCurrency(row.total_usd, 'USD ')
        ];
      }
      csvContent += rowData.map(escapeCSV).join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${invoice.invoice_id}_${activeTab.replace(/\s+/g, '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
        {infoItems.map((item, i) => (
          <Box key={i} sx={{ minWidth: 100 }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{item.label}</Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1e293b' }}>{item.value || '—'}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        {summaryItems.map((item, i) => (
          <Box key={i} sx={{ flex: 1, p: 1.5, bgcolor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', mb: 0.3 }}>{item.label}</Typography>
            {item.tooltip ? (
              <Tooltip title={item.tooltip} arrow placement="top">
                <Typography sx={{ fontSize: '16px', fontWeight: 800, color: item.color, letterSpacing: '-0.02em', cursor: 'help', display: 'inline-block', borderBottom: `1px dashed ${item.color}80` }}>
                  {item.value}
                </Typography>
              </Tooltip>
            ) : (
              <Typography sx={{ fontSize: '16px', fontWeight: 800, color: item.color, letterSpacing: '-0.02em' }}>{item.value}</Typography>
            )}
          </Box>
        ))}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { fontSize: '12px', fontWeight: 600, textTransform: 'none', minHeight: 36, padding: '6px 12px' },
            '& .Mui-selected': { color: '#7C2EA0 !important' },
            '& .MuiTabs-indicator': { backgroundColor: '#7C2EA0' },
          }}
        >
          {visibleTabs.map((tab, i) => (
            <Tab key={i} label={tab.label} />
          ))}
        </Tabs>
        <Tooltip title="Download CSV" placement="top" arrow>
          <span
            onClick={handleExportCSV}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              backgroundColor: "#f5f5f5",
              cursor: "pointer",
              opacity: 1,
              transition: "all 0.2s ease",
              marginRight: "8px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#e0e0e0";
            }}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
          >
            <PiFileCsv size={18} color="#333" />
          </span>
        </Tooltip>
      </Box>

      {visibleTabs.map((tab, i) => (
        <TabPanel key={i} value={tabValue} index={i}>
          {getTabContent(tab.label)}
        </TabPanel>
      ))}
    </Box>
  );
};

export default InvoiceDetailView;
